import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';
import { DrawerComponent } from '../../shared/drawer/drawer.component';
import { GridConfig } from '../../shared/dynamic-grid/grid-model/grid-config.model';
import { DrawerInputConfig } from '../../../models/drawer-model';
import { API_ENDPOINTS } from '../../shared/constants/apiEnpoints';
import { DynamicGridComponent } from '../../shared/dynamic-grid/dynamic-grid.component';
import { NIL as NIL_UUID } from 'uuid';
import { Toast, ToastComponent } from '../../shared/toast/toast.component';
import { Subscription } from 'rxjs';
import { RouteService } from '../../../service/route.service';
import { HttpClient } from '@angular/common/http';
import { MAX_INT_32 } from '../../shared/constants/values';

export interface PracticeLoadConfigurationData {
  id: string;
  practiceName?: string;
  loadScheduleName?: string;
  loadTypeName?: string;
  loadTypeId?: number;
  concurrentBatch: number;
  batchSize: number;
  isActive: boolean;
  loadScheduleId: string;
  practiceId: string;
  startCronExpression?: string;
  practiceOperationalHours?: string;
}

@Component({
  selector: 'app-practice-load-schedule',
  templateUrl: './practice-load-configuration.component.html',
  styleUrls: ['./practice-load-configuration.component.scss'],
})
export class PracticeLoadConfigurationComponent {
  drawerData: PracticeLoadConfigurationData = {
    id: NIL_UUID,
    practiceName: 'Select Practice',
    loadScheduleName: 'Select Load Schedule',
    loadTypeName: 'Select Load Type',
    loadTypeId: 1,
    concurrentBatch: 40,
    batchSize: 1000,
    isActive: true,
    loadScheduleId: NIL_UUID,
    practiceId: NIL_UUID,
    startCronExpression: '',
    practiceOperationalHours: '',
  };

  @ViewChild(DrawerComponent)
  drawerComponent!: DrawerComponent<PracticeLoadConfigurationData>;
  @ViewChild(DynamicGridComponent) dynamicGrid!: DynamicGridComponent;
  @ViewChild(ToastComponent) toastComponent!: ToastComponent;

  userRole = localStorage.getItem('userRole');

  gridConfig: GridConfig = {
    columns: [
      {
        label: 'Active',
        jsonKey: 'isActive',
        dataType: 'toggle',
        isSortable: false,
      },
      {
        label: 'Load Schedule Name',
        jsonKey: 'loadScheduleName',
        dataType: 'string',
      },
      {
        label: 'Load Type',
        jsonKey: 'loadTypeName',
        dataType: 'string',
      },
      { label: 'Practice Code', jsonKey: 'practiceCode', dataType: 'string' },
      {
        label: 'PRACTICE LOCAL TIME',
        jsonKey: 'description',
        dataType: 'string',
        width: 'full',
        isSortable: false,
      },
      {
        label: 'Timezone',
        jsonKey: 'timezone',
        dataType: 'string',
        width: 'full',
        isSortable: false,
      },
      {
        label: 'Concurrent Batches',
        jsonKey: 'concurrentBatch',
        dataType: 'number',
      },
      {
        label: 'Batch Size',
        jsonKey: 'batchSize',
        dataType: 'number',
      },
      {
        label: 'PMS Name',
        jsonKey: 'pmsName',
        dataType: 'string',
        width: 'full',
      },
      {
        label: 'Supported PMS Version',
        jsonKey: 'configuredPMSVersion',
        dataType: 'string',
        isSortable: false,
      },
      {
        label: 'Created By',
        jsonKey: 'createdByUserEmail',
        dataType: 'string',
        hideOnToggle: true,
      },
      {
        label: 'Created At',
        jsonKey: 'createdAt',
        dataType: 'date',
        hideOnToggle: true,
      },
      {
        label: 'Modified By',
        jsonKey: 'modifiedByUserEmail',
        dataType: 'string',
        hideOnToggle: true,
      },
      {
        label: 'Modified At',
        jsonKey: 'modifiedAt',
        dataType: 'date',
        hideOnToggle: true,
      },
    ],
    filters: [
      {
        label: 'PMS Name',
        jsonKey: 'pmsId',
        type: 'multi',
        options: [],
        width: 'w-50',
      },
    ],
    editable: true,
    deletable: false,
    displayAdd: true,
    isToggleDisabled: true,
    defaultFilters: { isActive: true },
    uniqueTableName: 'PracticeLoadConfiguration',
    displayHideAuditColumnsToggle: true
  };

  getDataUrl = API_ENDPOINTS.PRACTICE_LOAD_CONFIGURATION;
  toggleUrl = API_ENDPOINTS.TOGGLE_PRACTICE_LOAD_CONFIGURATION_ACTIVE;
  addDataUrl = API_ENDPOINTS.PRACTICE_LOAD_CONFIGURATION;
  editDataUrl = API_ENDPOINTS.PRACTICE_LOAD_CONFIGURATION;
  getPracticesUrl = API_ENDPOINTS.PMS_MASTER;

  drawerInputConfig: DrawerInputConfig[] = [
    {
      label: 'Practice Code',
      dataType: 'select',
      input: 'select',
      jsonKey: 'practiceCode',
      foreignKeyProperty: 'practiceId',
      dropDownOptionsURL: `${API_ENDPOINTS.PRACTICE_LOAD_CONFIGURATION_DROPDOWN}`,
      dropdownJsonKey: 'practiceNameWithPMS',
      dropdownSortDirection: 'asc',
      isRequired: true,
      isDropDownSearchable: true,
      uniqueIdentifier: 'NonSchedulePracticeCode'
    },
    {
      label: 'Load Schedule Name',
      dataType: 'select',
      input: 'select',
      jsonKey: 'loadScheduleName',
      foreignKeyProperty: 'loadScheduleId',
      dropDownOptionsURL: `${API_ENDPOINTS.LOAD_SCHEDULE_DROPDOWN}`,
      dropdownJsonKey: 'name',
      dropdownSortDirection: 'asc',
      isRequired: true,
      uniqueIdentifier: 'LoadScheduleName'
    },
    {
      label: 'Load Type',
      dataType: 'select',
      input: 'select',
      jsonKey: 'loadTypeName',
      foreignKeyProperty: 'loadTypeId',
      dropDownOptionsURL: `${API_ENDPOINTS.LOADTYPE}`,
      dropdownJsonKey: 'name',
      dropdownSortDirection: 'asc',
      isRequired: true,
      uniqueIdentifier: 'LoadTypeName'
    },
    {
      label: 'Concurrent Batches',
      jsonKey: 'concurrentBatch',
      input: 'number',
      dataType: 'number',
      isRequired: true,
      minLength: 1,
      maxLength: 99999
    },
    {
      label: 'Batch Size',
      jsonKey: 'batchSize',
      input: 'number',
      dataType: 'number',
      isRequired: true,
      minLength: 1,
      maxLength: 99999
    },
    {
      label: 'Active',
      jsonKey: 'isActive',
      input: 'toggle',
      dataType: 'boolean'
    },
  ];

  private _routerSubscription: Subscription;
  public get routerSubscription(): Subscription {
    return this._routerSubscription;
  }
  public set routerSubscription(value: Subscription) {
    this._routerSubscription = value;
  }
  routes: string[] = [];

  // Smart Schedule
  showSmartScheduleConfirm: boolean = false;
  isApplyingSchedule: boolean = false;
  highPerSlot: number = 1;
  mediumPerSlot: number = 2;
  lowPerSlot: number = 3;

  gridExtraActions = [
    {
      label: '⚡ Smart Schedule',
      action: () => this.openSmartSchedule(),
      cssClass: 'inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-500 hover:bg-indigo-400 text-white regular-font focus:outline-none focus:ring-2 focus:ring-indigo-500'
    }
  ];
  constructor(
    private routeService: RouteService,
    private cdr: ChangeDetectorRef,
    private _http: HttpClient
  ) { }

  ngAfterViewInit(): void {
    this.drawerComponent.drawerModelName = 'practiceLoadConfiguration';
    this._routerSubscription = this.routeService
      .getRoutes()
      .subscribe((data) => {
        this.routes = data;
        this.routes.shift();
        if (this.routes.length == 2) {
          this.routeService.redirectTO(
            this.routes.join('/').concat('/practice-load-configurations')
          );
        }
        this.cdr.detectChanges();
      });
  }

  fetchPMSNameOptions() {
    this._http
      .get<any>(this.getPracticesUrl, { params: { pageSize: MAX_INT_32 } })
      .subscribe((response) => {
        response.data = response.data
          .filter((option) => option.isActive)
          .map((option) => ({
            ...option,
            name: `${option.name} ${option.version}`,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        response.data = [{ id: 'All', name: 'All PMS', checked: true }, ...response.data];

        this.gridConfig.filters.find(
          (filter) => filter.jsonKey === 'pmsId'
        ).options = response;
      });
  }

  onAddHandler(addClicked: boolean): void {
    if (addClicked) {
      this.drawerComponent.drawerType = 'add';
      this.drawerInputConfig = this.drawerInputConfig.map((c, i) =>
        i === 0 ? { ...c, isDisabled: false } : c
      );
      this.resetEntry();
      this.openDrawer();
    }
  }

  openDrawer() {
    this.drawerComponent.openDrawer();
  }

  onEditHandler(rowData: any): void {
    if (this.drawerComponent) {
      this.drawerComponent.drawerType = 'edit';
      this.drawerInputConfig = this.drawerInputConfig.map((c, i) =>
        i === 0 ? { ...c, isDisabled: true } : c
      );
      const editModel = { ...rowData };
      editModel.practiceCode =
        rowData.practiceNameWithPMS ||
        (rowData.practiceCode && rowData.pmsName
          ? `${rowData.practiceCode} (${rowData.pmsName})`
          : rowData.practiceCode);

      if (editModel.loadTypeId == 2) {
        editModel.resetAllId = 1;
        editModel.resetAllName = 'Disabled';
      }

      this.drawerComponent.drawerModel = editModel;
      this.drawerComponent.openDrawer();
    } else {
      console.error('Drawer component is not initialized');
    }
  }

  resetEntry() {
    this.drawerComponent.drawerModel = {
      id: NIL_UUID,
      practiceName: 'Select Practice',
      loadTypeName: 'Select Load Type',
      loadTypeId: 1,
      loadScheduleName: 'Select Load Schedule',
      concurrentBatch: 40,
      batchSize: 1000,
      isActive: true,
      loadScheduleId: NIL_UUID,
      practiceId: NIL_UUID,
    };
  }

  refreshHandler() {
    this.dynamicGrid.fetchData();
  }

  ngOnInit(): void { this.fetchPMSNameOptions(); }

  showToast(toast: Toast) {
    this.toastComponent.showToast(toast);
    this.refreshHandler();
  }

  openSmartSchedule() {
    this.showSmartScheduleConfirm = true;
  }

  confirmApplySmartSchedule() {
    this.showSmartScheduleConfirm = false;
    this.isApplyingSchedule = true;
    const params = `?highPerSlot=${this.highPerSlot}&mediumPerSlot=${this.mediumPerSlot}&lowPerSlot=${this.lowPerSlot}`;
    this._http.post<any>(`${API_ENDPOINTS.PRACTICE_LOAD_CONFIGURATION_SMART_SCHEDULE}${params}`, {}).subscribe({
      next: (res: any) => {
        this.isApplyingSchedule = false;
        this.toastComponent.showToast({
          severity: 'success',
          title: 'Success',
          message: `Smart schedule applied. ${res.data?.totalUpdated ?? ''} configurations updated.`,
          duration: 4000
        });
        this.refreshHandler();
      },
      error: (err: any) => {
        this.isApplyingSchedule = false;
        this.toastComponent.showToast({
          severity: 'error',
          title: 'Error',
          message: err.error?.errorMessage || 'Failed to apply smart schedule',
          duration: 3000
        });
      }
    });
  }

  cancelSmartSchedule() {
    this.showSmartScheduleConfirm = false;
  }

  ngOnDestroy() {
    this._routerSubscription.unsubscribe();
  }
}
